import {
  createElement,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  FilterIcon,
  CheckIcon,
  Maximize2,
  Search,
  Settings,
  Download,
  X,
  Trash2,
  Calculator,
  Zap,
} from "lucide-react";
import { Checkbox } from "@headlessui/react";
import { format } from "date-fns";
import { ModalContainer } from "../ModalContainer";
import { TableSettings } from "./TableSettings";
import {
  ResponseDetailsSidePanel,
} from "./ResponseDetailsModal";
import { DownloadResponsesModal } from "./DownloadResponsesModal";
import { FilterResponsesModal } from "./FilterResponsesModal";
import { DateRangeSelector } from "./DateRangeSelector";
import { deleteResponses, getSurveyWithResponses } from "../../serverComm";
import debounce from "lodash/debounce";
import { ActionDialog } from "../ActionDialog";
import ConfirmationModal from "../ui/ConfirmationModal";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { TableSkeleton } from "./TableSkeleton";
import { renderTableAnswer } from "../../utils/helper";
import { sanitizeAnswerHtml, stripHtmlToText } from "../../utils/sanitizeHtml";
import inputTypes from "../../assets/inputTypes";

const DEFAULT_STATUS_FILTER = "all";

export const QUESTION_TYPE_ICONS = Object.keys(inputTypes).reduce((acc, key) => {
  acc[key] = {
    icon: inputTypes[key].icon,
    bgColor: inputTypes[key].bgColor,
    iconColor: inputTypes[key].iconColor,
  };
  return acc;
}, {});

export function SurveyAnalyticsTableView({ surveyId }) {
  const [surveyData, setSurveyData] = useState({});
  const { responses = [] } = surveyData;

  const [filters, setFilters] = useState({
    searchText: "",
    dateRange: null,
    activeFilters: [],
    statusFilter: DEFAULT_STATUS_FILTER,
  });

  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [showSettings, setShowSettings] = useState(false);
  const [visibleQuestions, setVisibleQuestions] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [showResponseDetails, setShowResponseDetails] = useState(false);
  const [currentResponseIndex, setCurrentResponseIndex] = useState(0);
  const [showDownload, setShowDownload] = useState(false);
  const [showDownloadSelected, setShowDownloadSelected] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [error, setError] = useState(null);
  const [hasHiddenRows, setHasHiddenRows] = useState(false);
  const tableRef = useRef(null);
  const fetchIdRef = useRef(0);

  const toggleSelect = (sid) => {
    const newSelected = selected.includes(sid)
      ? selected.filter((i) => i !== sid)
      : [...selected, sid];
    setSelected(newSelected);
    setShowActionDialog(newSelected.length > 0);
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    const newSelected = newSelectAll ? responses.map((sel) => sel._id) : [];
    setSelected(newSelected);
    setShowActionDialog(newSelected.length > 0);
  };

  const handleTableSettingsChange = (updatedQuestions) => {
    setVisibleQuestions(updatedQuestions);
    setShowSettings(false);
  };

  const handleShowResponse = (index) => {
    setCurrentResponseIndex(index);
    setShowResponseDetails(true);
  };

  const handleNextResponse = () => {
    setCurrentResponseIndex((prev) => Math.min(prev + 1, responses.length - 1));
  };

  const handlePreviousResponse = () => {
    setCurrentResponseIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleDownload = (_type, _format) => {
    setShowDownload(false);
    setShowDownloadSelected(false);
  };

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchSurveyData = useCallback(async (filterParams = {}) => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const requestParams = {
        ...filtersRef.current,
        ...filterParams,
        paginate: false,
      };
      const data = await getSurveyWithResponses(surveyId, requestParams);
      if (id !== fetchIdRef.current) return;
      setSurveyData(data);
      setHasHiddenRows(data.hasHiddenRows);
      const nonStatement = data.questions.filter(q => q.type !== "statement");
      setAllQuestions(nonStatement);
      setVisibleQuestions(nonStatement);
      setSelected([]);
      setSelectAll(false);
      setShowActionDialog(false);
    } catch (err) {
      if (id !== fetchIdRef.current) return;
      console.error("Error fetching survey data:", err);
      setError("Failed to fetch survey data");
    } finally {
      if (id === fetchIdRef.current) setLoading(false);
    }
  }, [surveyId]);

  const handleDateRangeChange = (start, end) => {
    if (!start || !end || isNaN(new Date(start).getTime()) || isNaN(new Date(end).getTime())) return;
    const newDateRange = { start, end };
    setFilters((prev) => ({ ...prev, dateRange: newDateRange }));
    fetchSurveyData({ dateRange: newDateRange });
  };

  const debouncedSearchRef = useRef(
    debounce((searchText) => {
      fetchSurveyData({ searchText });
    }, 500)
  );

  useEffect(() => {
    return () => debouncedSearchRef.current.cancel();
  }, []);

  const handleSearch = (searchText) => {
    setFilters((prev) => ({ ...prev, searchText }));
    debouncedSearchRef.current(searchText);
  };

  const handleApplyFilters = (newFilters) => {
    setFilters((prev) => ({ ...prev, activeFilters: newFilters }));
    setShowFilter(false);
    fetchSurveyData({ activeFilters: newFilters });
  };

  const clearDateFilter = () => {
    setFilters((prev) => ({ ...prev, dateRange: null }));
    fetchSurveyData({ dateRange: null });
  };

  const clearSearchFilter = () => {
    setFilters((prev) => ({ ...prev, searchText: "" }));
    fetchSurveyData({ searchText: "" });
  };

  const clearQuestionFilters = (e) => {
    e.stopPropagation();
    setFilters((prev) => ({ ...prev, activeFilters: [] }));
    setShowFilter(false);
    fetchSurveyData({ activeFilters: [] });
  };

  const clearAllFilters = () => {
    const reset = {
      searchText: "",
      dateRange: null,
      activeFilters: [],
      statusFilter: DEFAULT_STATUS_FILTER,
    };
    setFilters(reset);
    fetchSurveyData(reset);
  };

  const handleDeleteResponse = async () => {
    try {
      await deleteResponses(selected);
      setSurveyData((prev) => ({
        ...prev,
        responses: prev.responses.filter((res) => !selected.includes(res._id)),
      }));
      toast.success(`${selected.length > 1 ? `${selected.length} responses` : "Response"} deleted`);
      setSelected([]);
      setSelectAll(false);
      setShowActionDialog(false);
    } catch (err) {
      console.error("Error deleting response:", err);
      toast.error("Oops! Something went wrong.");
    }
  };

  useEffect(() => {
    fetchSurveyData({ statusFilter: filters.statusFilter });
  }, []);

  const hasCalculationRules = useMemo(() => {
    return surveyData?.rules?.some(
      (rule) =>
        rule.action === "add" ||
        rule.action === "subtract" ||
        rule.action === "multiply" ||
        rule.action === "divide"
    );
  }, [surveyData?.rules]);

  const originalIndexMap = useMemo(() => {
    const map = new Map();
    allQuestions.forEach((q, i) => map.set(q.id, i + 1));
    return map;
  }, [allQuestions]);

  const hasActiveFilters = filters.searchText ||
    filters.dateRange ||
    filters.activeFilters.length > 0 ||
    filters.statusFilter !== DEFAULT_STATUS_FILTER;

  if (error) return <div className="mx-auto px-8 py-4 text-center text-red-600">Error: {error}</div>;

  return (
    <div className="w-full px-8 py-4 -mt-[calc(var(--navbar-height,64px)+3.5rem)]">
      {loading ? (
        <TableSkeleton />
      ) : surveyData.surveyId && (
        <>
          <div className="flex justify-between items-center mb-3 text-sm">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <DateRangeSelector
                    onApply={handleDateRangeChange}
                    selectedRange={filters.dateRange}
                    onClear={clearDateFilter}
                  />
                  {filters.dateRange && (
                    <button
                      onClick={clearDateFilter}
                      className="p-1.5 hover:bg-gray-100 rounded-lg flex items-center justify-center transition-colors"
                      title="Clear date filter"
                    >
                      <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setShowFilter(true)}
                    className={`flex items-center gap-2 px-3 py-2 transition-all duration-200 ${
                      filters.activeFilters.length
                        ? "bg-blue-50 text-blue-600 border border-blue-200"
                        : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300"
                    } rounded-lg shadow-sm`}
                  >
                    <FilterIcon className="w-4 h-4" />
                    <span className="font-medium">
                      {filters.activeFilters.length
                        ? `${filters.activeFilters.length} filters`
                        : "Filter"}
                    </span>
                    {filters.activeFilters.length > 0 && (
                      <button
                        onClick={clearQuestionFilters}
                        className="p-1 hover:bg-blue-100 rounded-full flex items-center justify-center transition-colors"
                        title="Clear filters"
                      >
                        <X className="w-3 h-3 text-blue-500" />
                      </button>
                    )}
                  </button>
                </div>

                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={filters.searchText}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search responses"
                    className="pl-9 pr-8 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white shadow-sm transition-all"
                  />
                  {filters.searchText && (
                    <button
                      onClick={clearSearchFilter}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  <select
                    value={filters.statusFilter}
                    onChange={(e) => {
                      const newStatusFilter = e.target.value;
                      setFilters((prev) => ({ ...prev, statusFilter: newStatusFilter }));
                      fetchSurveyData({ statusFilter: newStatusFilter });
                    }}
                    className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white shadow-sm transition-all"
                  >
                    <option value="all">All responses</option>
                    <option value="completed">Completed only</option>
                    <option value="partial">Partial only</option>
                  </select>
                </div>

                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="text-sm text-gray-500 hover:text-gray-700 ml-2 font-medium transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
              <span className="text-xs text-gray-500">
                {responses.length} response{responses.length !== 1 ? 's' : ''}
              </span>
              <button
                onClick={() => setShowSettings(true)}
                className="p-2.5 hover:bg-gray-50 rounded-lg transition-colors group"
              >
                <Settings className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
              </button>
              <button
                onClick={() => setShowDownload(true)}
                className="p-2.5 hover:bg-gray-50 rounded-lg transition-colors group"
              >
                <Download className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden relative">
            <div 
              className="overflow-x-auto overflow-y-visible table-scroll-container" 
              style={{ maxWidth: '100%' }}
            >
              <table
                className="w-max min-w-full"
                ref={tableRef}
              >
                <thead>
                  <tr className="bg-gray-50/50 text-left text-xs">
                    <th className="px-3 py-3 border-b border-r border-gray-100 font-medium w-10">
                      <Checkbox
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className={`
                          ${selectAll ? "bg-blue-600" : "bg-white"}
                          form-checkbox relative w-5 h-5 border border-gray-300 rounded-md
                          transition-colors duration-200 ease-in-out
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                        `}
                      >
                        <span className="sr-only">Select all</span>
                        {selectAll && (
                          <CheckIcon className="h-3 w-3 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                        )}
                      </Checkbox>
                    </th>
                    <th className="px-3 py-3 border-b border-r border-gray-100 font-medium text-gray-600 min-w-28 text-xs">
                      Date
                    </th>
                    <th className="px-3 py-3 border-b border-r border-gray-100 font-medium text-gray-600 min-w-20 text-xs">
                      Status
                    </th>
                    {visibleQuestions
                      .filter((q) => q.visible !== false)
                      .map((question) => {
                        const qNum = originalIndexMap.get(question.id) || '';
                        const questionText = stripHtmlToText(question?.text) || '';
                        return (
                          <th
                            key={question.id}
                            className={`px-3 py-3 border-b border-r border-gray-100 font-medium text-gray-600 text-xs ${
                              questionText.length > 60 ? 'min-w-80 max-w-96' : questionText.length > 30 ? 'min-w-64 max-w-72' : 'min-w-48 max-w-56'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {QUESTION_TYPE_ICONS[question.type] && (
                                <div
                                  className={`flex items-center px-2 justify-between w-12 h-6 rounded-md ${
                                    QUESTION_TYPE_ICONS[question.type].bgColor
                                  } text-app-black text-sm flex-shrink-0`}
                                >
                                  {createElement(
                                    QUESTION_TYPE_ICONS[question.type].icon,
                                    { className: "w-3 h-3" }
                                  )}
                                  <span className="text-xs">{qNum}</span>
                                </div>
                              )}
                              <span className="font-medium text-gray-800 leading-tight break-words text-xs">
                                {questionText}
                              </span>
                            </div>
                          </th>
                        );
                      })}
                    {hasCalculationRules && (
                      <th className="px-3 py-3 border-b border-gray-100 font-medium text-gray-600 min-w-24 text-xs">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-md bg-purple-50 flex items-center justify-center flex-shrink-0 shadow-sm">
                            <Calculator className="h-3.5 w-3.5 text-purple-600" />
                          </div>
                          <span className="font-medium text-gray-800 text-xs">Score</span>
                        </div>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {responses.map((answer, idx) => (
                    <tr
                      key={answer._id || idx}
                      className={`${
                        answer.isHidden ? "hiddenRow" : ""
                      } group text-xs text-left border-b border-gray-100 hover:bg-gray-50/50 transition-colors 
                      ${selected.includes(answer._id) ? "bg-blue-50" : ""}`}
                    >
                      <td className="px-3 py-2.5 border-r border-gray-100 align-middle">
                        <Checkbox
                          checked={selected.includes(answer._id)}
                          onChange={() => toggleSelect(answer._id)}
                          className={`
                            ${selected.includes(answer._id) ? "bg-blue-600" : "bg-white"}
                            form-checkbox relative w-4 h-4 border border-gray-300 rounded-md
                            transition-colors duration-200 ease-in-out
                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                          `}
                        >
                          <span className="sr-only">Select row {idx + 1}</span>
                          {selected.includes(answer._id) && (
                            <CheckIcon className="h-2.5 w-2.5 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                          )}
                        </Checkbox>
                      </td>
                      <td className="px-3 py-2.5 relative align-middle border-r border-gray-100">
                        {answer.submittedAt ? (
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900 text-xs">
                              {format(new Date(answer.submittedAt), "dd MMM yyyy")}
                            </span>
                            <span className="text-gray-500 text-[11px]">
                              {format(new Date(answer.submittedAt), "hh:mm a")}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                        <button
                          onClick={() => handleShowResponse(idx)}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded-md"
                        >
                          <Maximize2 className="h-3.5 w-3.5 text-gray-500 hover:text-gray-700" />
                        </button>
                      </td>
                      <td className="px-3 py-2.5 align-middle border-r border-gray-100">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          answer.status === 'partial' 
                            ? 'bg-yellow-100 text-yellow-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {answer.status === 'partial' ? 'Partial' : 'Completed'}
                        </span>
                      </td>
                      {visibleQuestions
                        .filter((q) => q.visible !== false)
                        .map((question) => (
                          <td
                            key={question.id}
                            className={`px-3 py-2.5 align-middle border-r border-gray-100 text-xs ${
                              question?.text?.length > 60 ? 'max-w-96' : question?.text?.length > 30 ? 'max-w-72' : 'max-w-56'
                            } whitespace-normal break-words leading-relaxed`}
                            dangerouslySetInnerHTML={{
                              __html: sanitizeAnswerHtml(renderTableAnswer(
                                answer.answers?.[question?.id]
                              )),
                            }}
                          />
                        ))}
                      {hasCalculationRules && (
                        <td className="px-3 py-2.5 align-middle">
                          {answer.score !== undefined && answer.score !== null ? (
                            <div className="flex items-center">
                              <span className="font-medium text-gray-900 text-xs">
                                {answer.score}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasHiddenRows && (
              <>
                <BlurOverlay tableRef={tableRef} responses={responses} />
                <ResponseLimitMessage />
              </>
            )}
          </div>

          <ModalContainer
            title="Table Settings"
            isOpen={showSettings}
            onClose={() => setShowSettings(false)}
            size="lg"
          >
            <TableSettings
              questions={visibleQuestions}
              onApply={handleTableSettingsChange}
              onCancel={() => setShowSettings(false)}
            />
          </ModalContainer>

          <ResponseDetailsSidePanel
            isOpen={showResponseDetails}
            onClose={() => setShowResponseDetails(false)}
            response={responses[currentResponseIndex]}
            questions={visibleQuestions}
            currentIndex={currentResponseIndex}
            totalResponses={responses.length}
            onNext={handleNextResponse}
            onPrevious={handlePreviousResponse}
            endingScreens={surveyData?.endingScreens || []}
          />

          <ModalContainer
            isOpen={showDownload}
            onClose={() => setShowDownload(false)}
            title="Choose a file to download"
            size="md"
          >
            <DownloadResponsesModal
              totalResponses={responses.length}
              selectedCount={selected.length}
              filteredCount={responses.length}
              responses={responses}
              questions={visibleQuestions}
              selected={selected}
              onDownload={handleDownload}
              onCancel={() => setShowDownload(false)}
            />
          </ModalContainer>

          <ModalContainer
            isOpen={showFilter}
            onClose={() => setShowFilter(false)}
            title="Filter responses"
            size="md"
          >
            <FilterResponsesModal
              questions={visibleQuestions}
              responses={responses}
              onApply={handleApplyFilters}
              onCancel={() => setShowFilter(false)}
              activeFilters={filters.activeFilters}
            />
          </ModalContainer>

          <ActionDialog
            isOpen={showActionDialog}
            onClose={() => {
              setSelected([]);
              setSelectAll(false);
              setShowActionDialog(false);
            }}
          >
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                {selected.length} selected
              </span>
              <div className="h-6 w-px bg-gray-200" />
              <button
                className="text-gray-400 hover:text-gray-500"
                onClick={() => setShowDownloadSelected(true)}
              >
                <Download className="h-5 w-5" />
              </button>
              <button
                className="text-red-800 hover:text-red-700"
                onClick={() => setShowDeleteModal(true)}
              >
                <Trash2 className="h-5 w-5" />
              </button>
              <div className="h-6 w-px bg-gray-200" />
            </div>
          </ActionDialog>

          <ModalContainer
            isOpen={showDownloadSelected}
            onClose={() => setShowDownloadSelected(false)}
            title="Choose a file to download"
            size="md"
          >
            <DownloadResponsesModal
              totalResponses={responses.length}
              selectedCount={selected.length}
              filteredCount={responses.length}
              responses={responses}
              questions={visibleQuestions}
              selected={selected}
              onDownload={handleDownload}
              onCancel={() => setShowDownloadSelected(false)}
              forceTypeSelected={true}
            />
          </ModalContainer>

          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={() => handleDeleteResponse()}
            title={`Permanently delete ${
              selected.length > 1
                ? `${selected.length} responses`
                : "this response"
            }?`}
            message="Deleting responses is permanent and can't be undone."
          />
        </>
      )}
    </div>
  );
}

const BlurOverlay = ({ tableRef, responses }) => {
  const [overlayStyle, setOverlayStyle] = useState({ top: 0, height: '100%' });
  
  useEffect(() => {
    if (!tableRef.current) return;

    const hiddenRows = Array.from(
      tableRef.current.querySelectorAll(".hiddenRow")
    );
    
    if (hiddenRows.length === 0) {
      setOverlayStyle({ top: 0, height: '100%' });
      return;
    }

    const tableContainer = tableRef.current.closest('.table-scroll-container');
    if (!tableContainer) return;
    
    const firstRow = hiddenRows[0].getBoundingClientRect();
    const containerRect = tableContainer.getBoundingClientRect();
    
    const top = Math.max(0, firstRow.top - containerRect.top);

    setOverlayStyle({
      top,
      height: `calc(100% - ${top}px)`,
    });
  }, [responses]);

  return (
    <div
      id="blurOverlay"
      className="absolute backdrop-blur-sm left-0 w-full z-5 bg-white/60"
      style={{
        top: overlayStyle.top,
        height: overlayStyle.height,
      }}
    />
  );
};

const ResponseLimitMessage = () => {
  const navigate = useNavigate();

  return (
    <div
      className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10"
    >
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-4 py-2.5 flex items-center gap-3">
        <Zap size={16} className="text-purple-600 flex-shrink-0" />
        <span className="text-sm text-gray-700">Upgrade to view all responses</span>
        <button
          onClick={() => navigate('/profile?tab=billing')}
          className="text-xs cursor-pointer text-white bg-app-green-700 hover:bg-app-green-800 py-1.5 px-3 rounded-md font-medium transition-colors flex-shrink-0"
        >
          Upgrade
        </button>
      </div>
    </div>
  );
};
